import { Component, computed, ElementRef, HostListener, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { RoutePath } from '../../enums/common.enum';
import { LayoutService } from '../../../core/services/layout.service';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { RoleAccessService } from '../../services/role-access.service';

type SidebarChildItem = {
  label: string;
  path: string;
  adminOnly?: boolean;
};

type SidebarMenuItem = {
  id: string;
  label: string;
  icon: string;
  isOpen?: ReturnType<typeof signal<boolean>>;
  path?: string;
  children?: SidebarChildItem[];
};

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, SafeHtmlPipe],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css'
})
export class SidebarComponent {

  protected readonly layoutService = inject(LayoutService);
  private readonly roleAccessService = inject(RoleAccessService);
  private readonly elementRef = inject(ElementRef);
  protected readonly isSubAdmin = computed(() => {
    this.roleAccessService.permissionStateVersion();
    return this.roleAccessService.isSubAdmin();
  });

  isCollapsed = this.layoutService.isDesktopSidebarCollapsed;

  private readonly allMenuItems: SidebarMenuItem[] = [
    {
      id: 'sub-admin',
      label: 'Sub Admin Management',
      icon: `
        <svg class="w-6 h-6 ms-1" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M9 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-2 9a4 4 0 0 0-4 4v1a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1a4 4 0 0 0-4-4H7Zm8-1a1 1 0 0 1 1-1h1v-1a1 1 0 1 1 2 0v1h1a1 1 0 1 1 0 2h-1v1a1 1 0 1 1-2 0v-1h-1a1 1 0 0 1-1-1Z" clip-rule="evenodd"/>
        </svg>
      `,
      isOpen: signal(false),
      children: [
        { label: 'Sub Admins', path: '/' + RoutePath.SUB_ADMIN },
        { label: 'Role Delegation', path: '/' + RoutePath.SUB_ADMIN + '/' + RoutePath.ROLE_DELEGATION, adminOnly: true }
      ]
    },
    {
      id: 'role-delegation-tracker',
      label: 'Role Delegation Tracker',
      path: '/' + RoutePath.ROLE_DELEGATION_TRACKER,
      icon: `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v2H3V5Zm0 4h13v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Zm15 0h3a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2h-2V9Zm-9 4h4v2H9v-2Zm0-4h7v2H9V9Z"/>
        </svg>
      `
    },
    {
      id: 'delegation-history',
      label: 'Role Delegation History',
      path: '/' + RoutePath.DELEGATION_HISTORY,
      icon: `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7 3a1 1 0 0 1 1 1v1h8V4a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v2H3V7a2 2 0 0 1 2-2h1V4a1 1 0 0 1 1-1Z"/>
          <path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8Zm5 3a1 1 0 0 0 0 2h4a1 1 0 1 0 0-2H8Zm7 0a1 1 0 1 0 0 2h1a1 1 0 1 0 0-2h-1Z"/>
        </svg>
      `
    },
    {
      id: 'faq',
      path: '/' + RoutePath.FAQ,
      label: 'FAQ Management',
      icon: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.008-3.018a1.502 1.502 0 0 1 2.522 1.159v.024a1.44 1.44 0 0 1-1.493 1.418 1 1 0 0 0-1.037.999V14a1 1 0 1 0 2 0v-.539a3.44 3.44 0 0 0 2.529-3.256 3.502 3.502 0 0 0-7-.255 1 1 0 0 0 2 .076c.014-.398.187-.774.48-1.044Zm.982 7.026a1 1 0 1 0 0 2H12a1 1 0 1 0 0-2h-.01Z" clip-rule="evenodd"/>
</svg>
`,
      isOpen: signal(false),
      children: [
        { label: 'FAQ Categories', path: '/' + RoutePath.CATEGORY + '/' + RoutePath.FAQ_CATEGORY },
        { label: 'Manage FAQs', path: '/' + RoutePath.FAQ }
      ]
    },
    {
      id: 'blog',
      label: 'Content Room Management',
      icon: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
  <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
  <path d="M14 17H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
</svg>`,
      isOpen: signal(false),
      children: [
        { label: 'Content Room Categories', path: '/' + RoutePath.CATEGORY + '/' + RoutePath.BLOG_CATEGORY },
        { label: 'Manage Content Rooms', path: '/' + RoutePath.BLOG }
      ]
    },
    {
      id: 'partners',
      label: 'Partners Management',
      icon: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
</svg>`,
      isOpen: signal(false),
      children: [
        { label: 'Partner Categories', path: '/' + RoutePath.CATEGORY + '/' + RoutePath.PARTNER_CATEGORY },
        { label: 'Manage Partners', path: '/' + RoutePath.PARTNERS }
      ]
    },
    {
      id: 'audit-report',
      label: 'Audit Report Management',
      icon: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14H19zM7 10h2v2H7zm0-4h2v2H7zm0 8h2v2H7zm4-8h6v2h-6zm0 4h6v2h-6zm0 4h6v2h-6z"/>
      </svg>`,
      isOpen: signal(false),
      children: [
        { label: 'Audit Categories', path: '/' + RoutePath.CATEGORY + '/' + RoutePath.AUDIT_CATEGORY },
        { label: 'Manage Audit Reports', path: '/' + RoutePath.AUDIT_REPORT }
      ]
    },
    {
      id: 'banners',
      label: 'Banner Management',
      path: '/banners',
      icon: `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 14H5V6h14v12Z" clip-rule="evenodd"/>
          <path fill-rule="evenodd" d="M12 8a1 1 0 0 1 1 1v2.586l1.293-1.293a1 1 0 0 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L11 11.586V9a1 1 0 0 1 1-1Z" clip-rule="evenodd"/>
        </svg>
      `
    },
    {
      id: 'announcements',
      label: 'Announcement Management',
      path: '/' + RoutePath.ANNOUNCEMENT,
      icon: `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M18.458 3.11A1 1 0 0 1 19 4v16a1 1 0 0 1-1.581.814L12 16.944V7.056l5.419-3.87a1 1 0 0 1 1.039-.076ZM22 12c0 1.48-.804 2.773-2 3.465v-6.93c1.196.692 2 1.984 2 3.465ZM10 8H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h6V8Zm0 9H5v3a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-3Z" clip-rule="evenodd"/>
        </svg>
      `
    },
    {
      id: 'vote-domain',
      label: 'Vote Domain Management',
      path: '/' + RoutePath.VOTE_DOMAIN,
      icon: `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      `
    },
    {
      id: 'docs',
      label: 'Docs Management',
      path: '/' + RoutePath.DOCS,
      icon: `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.414a2 2 0 0 0-.586-1.414l-3.414-3.414A2 2 0 0 0 13.586 3H7Zm2 6a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2H9Zm0 4a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H9Zm0 4a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H9Z" clip-rule="evenodd"/>
        </svg>
      `
    }
  ];

  protected readonly menuItems = computed<SidebarMenuItem[]>(() => {
    this.roleAccessService.permissionStateVersion();
    if (!this.roleAccessService.isSubAdmin()) {
      return this.allMenuItems;
    }

    const allowedRoles = new Set(this.roleAccessService.getActiveRoleIds());
    return this.allMenuItems
      .filter(item => allowedRoles.has(item.id))
      .map(item => {
        if (!item.children) return item;
        return {
          ...item,
          children: item.children.filter(child => !child.adminOnly)
        };
      });
  });

  /**
   * Toggle the open state of a menu item with children.
   *
   * @param item The menu item to toggle.
   */
  toggleSubMenu(item: SidebarMenuItem) {
    if (item.children) {
      const isCurrentlyOpen = item.isOpen?.() || false;
      this.menuItems().forEach(menuItem => {
        if (menuItem !== item && menuItem.isOpen) {
          menuItem.isOpen.set(false);
        }
      });
      item.isOpen?.set(!isCurrentlyOpen);
    }
  }

  isMenuOpen(item: SidebarMenuItem): boolean {
    return item.isOpen?.() ?? false;
  }

  onChildClick(item: SidebarMenuItem) {
    if (this.isCollapsed() && item.isOpen) {
      item.isOpen.set(false);
    }
    this.layoutService.closeMobileSidebar();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.menuItems().forEach(menuItem => {
        if (menuItem.isOpen) {
          menuItem.isOpen.set(false);
        }
      });
    }
  }

  /**
   * Toggle the collapse state of the sidebar.
   */
  toggleCollapse() {
    this.layoutService.toggleDesktopCollapse();
  }

}
